import { Link } from "react-router-dom";

import {
  actorEmail,
  auditStamp,
  auditTarget,
  blockedUserCount,
  type McpTenantSummary,
} from "../../../api/mcp";
import { BlockControl } from "../../../components/mcp/BlockControl";
import { Reason } from "../../../components/write/ReasonText";
import { ResourceView } from "../../../components/states";
import { Badge, Card, Empty, Table, Td, Th } from "../../../components/ui";
import type { AppConfig } from "../../../config";
import { Button, Input } from "../../../f7";
import type { AnyAuditEvent } from "../../../hooks/useAuditFeed";
import type { PageOf } from "../../../hooks/usePagedList";
import type { Resource } from "../../../hooks/useResource";
import type { TenantDetailsByRef } from "../../../hooks/useTenantDetails";
import { ProductSection } from "../../../products/PerProduct";
import { BlockedIpsPanel } from "./BlockedIps";

/** The most blocked users one merchant's row names; past it, the row shows the count. */
const LISTED_USERS_MAX = 10;

export function McpAccess({
  config,
  scope,
  everywhere,
  blockedMerchants,
  withUserBlocks,
  fannedOutDetails,
  reload,
  blockAudit,
  blockEmail,
  setBlockEmail,
  merchantRef,
  setMerchantRef,
  email,
  setEmail,
  checkRef,
  checkEmail,
}: {
  config: AppConfig;
  scope: "merchants" | "users" | null;
  everywhere: string[];
  blockedMerchants: McpTenantSummary[];
  withUserBlocks: McpTenantSummary[];
  fannedOutDetails: TenantDetailsByRef;
  reload: () => void;
  blockAudit: { state: Resource<PageOf<AnyAuditEvent>>; reload: () => void };
  blockEmail: string;
  setBlockEmail: (value: string) => void;
  merchantRef: string;
  setMerchantRef: (value: string) => void;
  email: string;
  setEmail: (value: string) => void;
  checkRef: string;
  checkEmail: string;
}) {
  return (
    <ProductSection id="mcp" note="Who is refused tool calls, and at what scope">
    {/* The design's card grid, not a stack of bare sections under
        uppercase micro-headings. Each scope is a panel of the same
        shape as every other panel in the console. */}
    <div className="grid gap-4 lg:grid-cols-2">
      {/* First, because it is the widest scope and belongs to no
          merchant — attaching it per-merchant would misreport it.
          Hidden when the metric row has narrowed to the other
          scope: the two are genuinely different facts, and showing
          both under a filter for one would undo the filter. */}
      {scope !== "merchants" && (
      <Card
        title="Blocked across every merchant"
        note={
          <>
            A <span className="ref">("*", email)</span> row. These people are refused
            everywhere, including at merchants not listed here. Unblocking them at one
            merchant will not lift this.
          </>
        }
      >
        {everywhere.length === 0 ? (
          <Empty>Nobody.</Empty>
        ) : (
          <Table
            className="[--gutter:0px]"
            head={<tr><Th>Email</Th><Th right>Action</Th></tr>}
          >
            {everywhere.map((e) => (
              <tr key={e}>
                <Td>
                  <span className="ref">{e}</span>
                </Td>
                <Td right>
                  <BlockControl
                    config={config}
                    scope={{ kind: "everywhere", email: e }}
                    blocked
                    onDone={reload}
                  />
                </Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      )}
      {scope !== "users" && (
      <Card
        title="Blocked merchants"
        note={
          <>
            A <span className="ref">("&lt;reference&gt;", "*")</span> row — every user
            of that merchant is refused.
          </>
        }
      >
        {blockedMerchants.length === 0 ? (
          <Empty>None.</Empty>
        ) : (
          <Table
            className="[--gutter:0px]"
            head={
              <tr>
                <Th>Reference</Th>
                <Th>Name</Th>
                <Th>Why</Th>
              </tr>
            }
          >
            {blockedMerchants.map((t) => {
              const detail = fannedOutDetails[t.business_reference];
              return (
                <tr key={t.business_reference}>
                  <Td>
                    <Link
                      to={`/tenants/${encodeURIComponent(t.business_reference)}`}
                      className="ref"
                    >
                      {t.business_reference}
                    </Link>
                  </Td>
                  <Td>{t.business_name}</Td>
                  <Td>
                    {detail?.blocked_reason ? (
                      <span className="text-xs text-slate-500 dark:text-slate-400">
                        <Reason text={detail.blocked_reason} />
                        {detail.blocked_by && (
                          <span className="ref ml-1">by {detail.blocked_by}</span>
                        )}
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400 dark:text-slate-500">—</span>
                    )}
                  </Td>
                </tr>
              );
            })}
          </Table>
        )}
      </Card>
      )}
    </div>

    {/* A (ref, email) row is NEITHER of the two counts above — the
        metric buckets merchant-wide and everywhere, and this scope
        is the third. Shown under the "users" filter, because that is
        the question it answers. */}
    {scope !== "merchants" && (
    <Card
      title="Users blocked at one merchant"
      note={
        <>
          A <span className="ref">("&lt;reference&gt;", email)</span> row — that person
          is refused at that merchant only.
        </>
      }
    >
      {withUserBlocks.length === 0 ? (
        <Empty>None.</Empty>
      ) : (
        <Table
          className="[--gutter:0px]"
          head={
            <tr>
              <Th>Merchant</Th>
              <Th>Blocked users</Th>
            </tr>
          }
        >
          {withUserBlocks.map((t) => {
            // Names from the list when it still carries them, else
            // from the per-merchant fan-out this page ran for
            // exactly this reason — MCP moved names off the list
            // onto tenant detail (8127fde), and dev already made
            // the move.
            const names = t.blocked_users ?? fannedOutDetails[t.business_reference]?.blocked_users;
            // Past LISTED_USERS_MAX names the row stops being a glance: one
            // merchant with dozens pushed every other row off the screen
            // (DA-3866). The merchant's own users page lists them all.
            const count = names ? names.length : blockedUserCount(t);
            const listed = names && names.length <= LISTED_USERS_MAX ? names : null;

            return (
              <tr key={t.business_reference}>
                <Td>
                  <Link
                    to={`/tenants/${encodeURIComponent(t.business_reference)}/users`}
                    className="ref"
                  >
                    {t.business_reference}
                  </Link>
                  <div className="text-xs text-slate-500 dark:text-slate-400">{t.business_name}</div>
                </Td>
                <Td>
                  {!listed ? (
                    <Link
                      to={`/tenants/${encodeURIComponent(t.business_reference)}/users`}
                      className="text-xs"
                    >
                      {count} blocked user
                      {count === 1 ? "" : "s"} — open the merchant to see
                      who
                    </Link>
                  ) : (
                    <ul className="space-y-0.5">
                      {listed.map((e) => (
                        <li key={e}>
                          <Link
                            to={`/tenants/${encodeURIComponent(
                              t.business_reference,
                            )}/users/${encodeURIComponent(e)}`}
                            className="ref text-xs"
                          >
                            {e}
                          </Link>
                          {everywhere.includes(e) && (
                            <span className="ml-2 text-xs text-amber-700 dark:text-amber-400">
                              also blocked everywhere
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </Td>
              </tr>
            );
          })}
        </Table>
      )}
    </Card>
    )}

    {/* Who blocked whom, when, and why. Absent from every table
        above by construction — a blocklist row is DELETED on
        unblock, so nothing else here remembers that someone was
        ever blocked or who lifted it. This is `GET
        /admin/audit/tenant`, the fleet-wide feed for the tenant
        domain, which carries every block/unblock action alongside
        tier changes. */}
    <Card
      title="Who blocked whom"
      note="From the audit log · tenant domain, fleet-wide · block and unblock actions"
    >
      <ResourceView
        state={blockAudit.state}
        what="the block history"
        onRetry={blockAudit.reload}
      />

      {blockAudit.state.kind === "ready" &&
        (() => {
          const rows = blockAudit.state.data.rows.filter(
            (e) => e.action === "block" || e.action === "unblock",
          );
          if (rows.length === 0) {
            return <Empty>No block or unblock actions in the last 7 days.</Empty>;
          }
          return (
            <Table
              className="[--gutter:0px]"
              head={
                <tr>
                  <Th>When</Th>
                  <Th>Who</Th>
                  <Th>Action</Th>
                  <Th>Target</Th>
                </tr>
              }
            >
              {rows.slice(0, 10).map((e, i) => (
                <tr key={`${e.timestamp}-${i}`}>
                  <Td>
                    <span className="text-xs text-slate-500 dark:text-slate-400">
                      {auditStamp(e.timestamp)}
                    </span>
                  </Td>
                  <Td>
                    <span className="text-xs">{actorEmail(e.actor)}</span>
                  </Td>
                  <Td>
                    <Badge tone={e.action === "block" ? "red" : "green"}>
                      {e.action}
                    </Badge>
                  </Td>
                  <Td>
                    {auditTarget(e.target) ? (
                      <Link
                        to={`/tenants/${encodeURIComponent(auditTarget(e.target)!)}`}
                        className="ref text-xs"
                      >
                        {auditTarget(e.target)}
                      </Link>
                    ) : (
                      <span className="text-xs text-slate-400 dark:text-slate-500">—</span>
                    )}
                  </Td>
                </tr>
              ))}
            </Table>
          );
        })()}
    </Card>

    <div className="grid gap-4 lg:grid-cols-2">
      <Card
        title="Block someone everywhere"
        note={
          <>
            Writes a <span className="ref">("*", email)</span> row. This control lives
            only here, never on a merchant page — the widest block should not be
            reachable by muscle memory while looking at one merchant.
          </>
        }
      >
        <div className="flex flex-wrap items-center gap-2">
          {/* Width on a wrapper — F7's Input is a `w-full` span, so a
              `w-72` on the Input itself never applied. */}
          <div className="w-72">
            <Input
              value={blockEmail}
              onChange={(ev) => setBlockEmail(ev.target.value)}
              placeholder="email@example.com"
              aria-label="Email to block everywhere"
            />
          </div>
          {blockEmail.trim() && (
            <BlockControl
              config={config}
              scope={{ kind: "everywhere", email: blockEmail.trim().toLowerCase() }}
              blocked={false}
              onDone={() => {
                setBlockEmail("");
                reload();
              }}
            />
          )}
        </div>
        <p className="mt-2 text-[11px] text-slate-400 dark:text-slate-500">
          Stored lowercase, which is how the server matches it.
        </p>
      </Card>

      {/* Answers a support question definitively rather than by
          inference across the three tables above — it reads the same
          check the running server uses. */}
      <Card
        title="Would this person be refused right now?"
        note="Checks all three scopes at once, the same way the running server does."
      >
        <div className="flex flex-wrap items-center gap-2">
          <div className="w-40">
            <Input
              value={merchantRef}
              onChange={(e) => setMerchantRef(e.target.value)}
              placeholder="Merchant reference"
              aria-label="Merchant reference"
            />
          </div>
          <div className="w-64">
            <Input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@example.com"
              aria-label="Email to check"
            />
          </div>
          {/* The enable check and the URL read the SAME trimmed
              values. They used to disagree — enabled on `ref.trim()`
              while the link was built from raw `ref` — so a reference
              pasted from a spreadsheet with a trailing space enabled
              the button and resolved to `…/507077%20/users/…`, which
              404s and reads as "no such merchant" rather than "stray
              whitespace". */}
          <Button
            href={
              checkRef && checkEmail
                ? `/tenants/${encodeURIComponent(checkRef)}/users/${encodeURIComponent(
                    checkEmail.toLowerCase(),
                  )}`
                : undefined
            }
            disabled={!checkRef || !checkEmail}
          >
            Check
          </Button>
        </div>
      </Card>

      {/* A network address, not a merchant or a user: it belongs to neither
          scope, so the metric row's filter hides it like the other scope. */}
      {scope === null && (
        <div className="lg:col-span-2">
          <BlockedIpsPanel config={config} />
        </div>
      )}
    </div>

    </ProductSection>
  );
}
